/**
 * MobX store for Manage Returns list filters: search, drawer filters, quick status chips, and Open/Closed/All tab (status_group).
 * Initial search can be seeded from URL query `search_input` or, when that is absent, `orderId`.
 * Optional default date range (previous N calendar months ending today, merchant TZ else UTC) is
 * enabled per page via constructor options; the redesigned list opts in, the legacy list does not.
 */
// eslint-disable-next-line
import { makeAutoObservable, runInAction } from "mobx";
import {GroupedOption, IOption} from "../../../../components/inputs/select-dropdown-grouped/select-dropdown-grouped";
import RmaListService, { IFetchRmaFiltersArgs, IRmaListFilterOptions, IRmaListTab, IReturnReason, MANAGE_RETURNS_BACKEND, PAYMENT_STATUS_TYPE, RESERVED_ITEM_EXPIRATION_TYPE, RESTOCK_VALIDATED_TYPE } from "../../../../services/api/rma-list-service";
import { TYPE, rootContainer } from "../../../../services/container/root";
import { IChangeable } from "../../../../stores/types";
import { RmaListBucketFiltersStore } from "./rma-list-bucket-filters-store";
import { QUICK_FILTER_CHIPS, RMA_QUICK_FILTERS, isQuickFilterChip } from "./rma-quick-filters";
import { isQuickFilterAllowedOnTab } from "./rma-tab-quick-filters";
import dashboardTranslationsStore from "../../../../dashboard-translation/stores/dashboard-translations-store";
import { IReportFiltersType } from "../../../../services/api/report-service";
import { BUTTON_VARIANTS } from "../../../../components/inputs/button-variants";
import {
    FALLBACK_FORMAT_PARAMS,
    formatChipCalendarDate,
    formatDate,
    isoRangeStringToCalendarRange,
    previousMonthsIsoRangeString,
} from "../../../../utilities/date-utilities";
import { ReservedItemDaysRemainingStore } from "../components/rma-list-filters-drawer/reserved-item-expiration-dropdown/reserved-item-days-remaining-store";
import ResolutionsPreset from "../../../policies/stores/presets/resolutions-preset";
import currentPortalStore from "../../../../stores/current-portal-store";
import { FEATURES } from "../../../../models/Plan";
import { getReservedItemSelectedLabel } from "../../rma-list-page-new/components/rma-list-filters-sidebar/components/rma-list-filter-reserved-item/get-reserved-item-selected-label";
import {
    IRmaListFilterControlOption,
    IRmaListFilterControlOptionGroup,
} from "../../rma-list-page-new/components/rma-list-filters-sidebar/components/rma-list-filter-control/components/rma-list-filter-control-portal/rma-list-filter-control-content/rma-list-filter-control-content";

/** One chip per redesigned-sidebar filter, in sidebar order. */
export enum SELECTED_FILTER_CHIP {
    STATUS = "status",
    DATE_RANGE = "date_range",
    RESOLUTION = "resolution",
    RETURN_REASON = "return_reason",
    VALIDATED = "validated",
    RESERVED_ITEM = "reserved_item",
}

/** How a selected-filter chip relates to the committed filters versus the draft. */
export enum SELECTED_FILTER_CHIP_STATE {
    /** Committed and still selected in the draft. */
    APPLIED = "applied",
    /** Selected in the draft, not committed. */
    DRAFT = "draft",
    /** Committed, but cleared or replaced in the draft. Apply has not run. */
    REMOVED = "removed",
}

export interface ISelectedFilterChip {
    id: SELECTED_FILTER_CHIP;
    label: string;
    state: SELECTED_FILTER_CHIP_STATE;
}

export interface IRmaQuickFilterStatusOption {
    id: RMA_QUICK_FILTERS;
    title: string;
}

export interface RmaListFiltersStoreOptions {
    /** When set, seeds Date created with the previous N calendar months on load. */
    defaultDateRangeMonths?: number;
    /** Backend serving the page that owns this store. Defaults to the proxy, which the legacy list uses. */
    backend?: MANAGE_RETURNS_BACKEND;
}

interface IRmaListFilters {
    search_input: string,
    search_exact_match: boolean,
    filter_resolution_id: number | null,
    filter_return_reason_id: number | null,
    filter_restock_validated_type: RESTOCK_VALIDATED_TYPE,
    filter_reserve_item_expiration: RESERVED_ITEM_EXPIRATION_TYPE,
    filter_date_range: string,
    filter_reserve_item_expiration_input: string,
    filter_status?: string,
    filter_payment_status_type: PAYMENT_STATUS_TYPE,
    filter_manual_actions: string[],
    filter_error_labels: string[],
    filter_on_track: string[],
    status_group: IRmaListTab,
}
interface IRmaListFiltersChanges extends Partial<IRmaListFilters> { };

export interface IFiltersButtonstyle {
    variant: BUTTON_VARIANTS,
    color: string,
}

class RmaListFiltersStore implements IChangeable<IRmaListFilters> {
    private _isLoaded: boolean = false;
    private _isLoading: boolean = false;
    private _returnReasonOptions?: IReturnReason[] = [];
    private _resolutionsPreset: ResolutionsPreset = {} as ResolutionsPreset;
    /** Sidebar resolution filter groups (excludes synthetic "All"); built once when filter options load. */
    private _resolutionFilterGroups: IRmaListFilterControlOptionGroup[] = [];
    /** Cached selected option for the sidebar chip label; updated when selection changes. */
    private _selectedResolutionOption: IRmaListFilterControlOption | null = null;
    /** Sidebar validated filter options (excludes synthetic "All"); built once on first access. */
    private _restockValidatedFilterOptions: IRmaListFilterControlOption[] = [];
    /** Cached selected option for the sidebar validated chip label; updated when selection changes. */
    private _selectedRestockValidatedOption: IRmaListFilterControlOption | null = null;
    private _model: IRmaListFilters;
    private _changes: IRmaListFiltersChanges = {};
    private _scope = 'dashboard-returns-page';
    private _reservedItemDaysRemainingStore: ReservedItemDaysRemainingStore = new ReservedItemDaysRemainingStore();
    private readonly _defaultDateRangeMonths: number | null;
    public readonly backend: MANAGE_RETURNS_BACKEND;
    /** True until the merchant changes or clears Date created; used to keep the default window aligned with TZ. */
    private _isDefaultDateRange: boolean;
    /** Guards concurrent first loads; refetches have their own id on the bucket store. */
    private _filterOptionsRequestId: number = 0;
    /** Redesigned-page-only sub-filter; see RmaListBucketFiltersStore. */
    private _bucketFilters: RmaListBucketFiltersStore = new RmaListBucketFiltersStore(this);

    constructor(options: RmaListFiltersStoreOptions = {}) {
        this._defaultDateRangeMonths = options.defaultDateRangeMonths ?? null;
        this.backend = options.backend ?? MANAGE_RETURNS_BACKEND.LARAVEL_PROXY;
        this._isDefaultDateRange = this._defaultDateRangeMonths !== null;
        makeAutoObservable(this, {}, { autoBind: true });
        this._model = this._createModelFromQuery();
    }

    protected get _rmaListService(): RmaListService { return rootContainer.get<RmaListService>(TYPE.RmaListService); }

    public get searchInput() {
        return this._changes.search_input ?? this._model.search_input;
    }

    private get status(): string | undefined {
        return this._changes.filter_status ?? this._model.filter_status;
    }

    public get searchURLParams(): URLSearchParams {
        const search = this.searchInput;
        const status = this.status;
        const newUrlSearchParams = new URLSearchParams();

        if (search) {
            newUrlSearchParams.set('search_input', search);
        }

        if (status) {
            newUrlSearchParams.set('filter_status', status);
        }

        return newUrlSearchParams;
    }

    public get searchExactMatch() {
        if (this._changes.search_exact_match === false) {
            return false;
        }
        return this._changes.search_exact_match ?? this._model.search_exact_match;
    }

    get isLoading() {
        return this._isLoading;
    }

    get areFilterOptionsLoaded() {
        return this._isLoaded;
    }

    get currentTab(): IRmaListTab {
        return this._changes.status_group ?? this._model.status_group;
    }

    get selectedResolutionId() {
        if (this._changes.filter_resolution_id === null) {
            return null;
        }
        return this._changes.filter_resolution_id ?? this._model.filter_resolution_id;
    }

    get selectedReturnReasonId() {
        if (this._changes.filter_return_reason_id === null) {
            return null;
        }
        return this._changes.filter_return_reason_id ?? this._model.filter_return_reason_id;
    }

    get restockValidatedType() {
        return this._changes.filter_restock_validated_type ?? this._model.filter_restock_validated_type;
    }

    /** Selected validated id for the redesigned sidebar filter; `null` when "All". */
    public get selectedRestockValidatedId(): RESTOCK_VALIDATED_TYPE | null {
        if (this.restockValidatedType === RESTOCK_VALIDATED_TYPE.ALL) {
            return null;
        }
        return this.restockValidatedType;
    }

    get reserveItemExpiration() {
        return this._changes.filter_reserve_item_expiration ?? this._model.filter_reserve_item_expiration;
    }

    get reserveItemExpirationInput() {
        return this._changes.filter_reserve_item_expiration_input ?? this._model.filter_reserve_item_expiration_input;
    }

    public get reserveItemExpirationFilterValid() {
        if (this.reserveItemExpiration === RESERVED_ITEM_EXPIRATION_TYPE.DATE_RANGE && !this.reserveItemExpirationInput) {
            return false;
        }
        if (this.reserveItemExpiration === RESERVED_ITEM_EXPIRATION_TYPE.DAYS_REMAINING) {
            const { from, to } = this._reservedItemDaysRemainingStore.getUpdatedModel();
            if (from === undefined || to === undefined || from >= to) {
                return false;
            }
        }
        return true;
    }

    get dateRange() {
        return this._changes.filter_date_range ?? this._model.filter_date_range;
    }

    /**
     * Chip label for the selected date range, formatted in the given IANA time zone.
     * Returns undefined when no range is set or parsing fails.
     */
    public getDateRangeSelectedChipLabel(timeZone: string): string | undefined {
        return this._dateRangeChipLabel(this.dateRange, timeZone);
    }

    private _dateRangeChipLabel(rangeString: string, timeZone: string): string | undefined {
        const range = isoRangeStringToCalendarRange(rangeString, timeZone);
        if (!range) {
            return undefined;
        }

        return `${formatChipCalendarDate(range.from)} - ${formatChipCalendarDate(range.to)}`;
    }

    /**
     * MM/DD/YYYY input pair for the selected date range in the given IANA time zone.
     * Returns empty strings when no range is set or parsing fails.
     */
    public getDateRangeInputValues(timeZone: string): { from: string; to: string } {
        const range = isoRangeStringToCalendarRange(this.dateRange, timeZone);
        if (!range) {
            return { from: "", to: "" };
        }

        return {
            from: formatDate(range.from),
            to: formatDate(range.to),
        };
    }

    /**
     * Count of applied (committed) wired sidebar filters that are non-default for Clear all (N).
     * Status counts when a chip is selected; date counts when a range is applied; resolution /
     * return reason / validated / reserved-item expiration when set.
     *
     * Each Action-required bucket counts as one group, not one per chip. Load-bearing: Clear all
     * disables itself at 0, so without it a bucket-only filter could not be cleared.
     */
    public get appliedWiredSidebarFilterCount(): number {
        return [
            isQuickFilterChip(this._model.filter_status),
            this._model.filter_date_range,
            this._model.filter_resolution_id,
            this._model.filter_return_reason_id,
            this._model.filter_restock_validated_type &&
                this._model.filter_restock_validated_type !== RESTOCK_VALIDATED_TYPE.ALL,
            this._model.filter_reserve_item_expiration &&
                this._model.filter_reserve_item_expiration !== RESERVED_ITEM_EXPIRATION_TYPE.ALL,
            this._bucketFilters.appliedManualActions.length,
            this._bucketFilters.appliedErrorLabels.length,
            this._bucketFilters.appliedOnTrack.length,
        ]
            .map(Boolean)
            .reduce((acc, curr) => acc + Number(curr), 0);
    }

    /**
     * Count of wired sidebar filters with uncommitted draft changes (status, date, resolution,
     * return reason, validated, reserved-item expiration). Used for Apply filters (N) and to
     * disable Apply when nothing is pending.
     *
     * Excludes the Action-required buckets: they apply on click, so counting them would light up
     * "Apply filters (1)" for something already applied.
     */
    public get draftWiredSidebarFilterCount(): number {
        return [
            this._changes.filter_status !== undefined,
            this._changes.filter_date_range !== undefined,
            this._changes.filter_resolution_id !== undefined,
            this._changes.filter_return_reason_id !== undefined,
            this._changes.filter_restock_validated_type !== undefined,
            this._changes.filter_reserve_item_expiration !== undefined ||
                this._changes.filter_reserve_item_expiration_input !== undefined ||
                this._reservedItemDaysRemainingStore.hasChanges(),
        ]
            .map(Boolean)
            .reduce((acc, curr) => acc + Number(curr), 0);
    }

    get paymentStatus() {
        return this._changes.filter_payment_status_type ?? this._model.filter_payment_status_type;
    }

    get selectedQuickFilter() {
        return this._changes.filter_status ?? this._model.filter_status;
    }

    /**
     * Status chips for the redesigned sidebar, ordered for display and filtered by the active tab.
     * Manager-approval chips are included only when the portal requires manager approval
     * (`currentPortalStore.requireManagerApproval()`), which is an observable — keeping this as a
     * computed getter ensures the chip list re-renders when that setting changes.
     */
    public getVisibleQuickFilterStatuses(translate: (key: string) => string): IRmaQuickFilterStatusOption[] {
        const requireManagerApproval = currentPortalStore.requireManagerApproval();

        return QUICK_FILTER_CHIPS
            .filter(chip => !chip.requiresManagerApproval || requireManagerApproval)
            .filter(chip => isQuickFilterAllowedOnTab(this.currentTab, chip.id))
            .map(chip => ({ id: chip.id, title: translate(chip.translationKey) }));
    }

    private get _allOption() {
        return {
            label: dashboardTranslationsStore.getTranslation('ALL', this._scope),
            value: 0 /** Select dropdown options doesn't accept null */
        }
    }

    get resolutionsOptions(): GroupedOption[] {
        if (!this._isLoaded) {
            return [];
        }
        return [
            {category: '', options: [this._allOption]},
            ...this._resolutionsPreset.getOptionsPreset()
        ];
    }

    /** Options for the redesigned sidebar resolution filter (excludes the synthetic "All" option). */
    public get resolutionFilterGroups(): IRmaListFilterControlOptionGroup[] {
        return this._resolutionFilterGroups;
    }

    public get selectedResolutionLabel(): string | undefined {
        return this._selectedResolutionOption?.label;
    }

    get selectedResolutionValue(): IOption | undefined {
        if (!this.selectedResolutionId) {
            return this._allOption;
        }

        if (!this._isLoaded) {
            return undefined;
        }

        const selectedResolution = this._resolutionsPreset.getPresetById(this.selectedResolutionId);
        if (!selectedResolution) {
            return undefined;
        }

        return {
            label: selectedResolution.internal_name,
            value: selectedResolution.id
        };
    }

    get returnReasonsOptions() {
        if (!this._returnReasonOptions) {
            return [];
        }
        return [
            this._allOption,
            ...this._returnReasonOptions?.map(returnReason => (
                {
                    label: returnReason.name,
                    value: returnReason.id
                }
            ))
        ];
    }

    /** Options for the redesigned sidebar return-reason filter (excludes the synthetic "All" option). */
    public get returnReasonFilterOptions(): IRmaListFilterControlOption[] {
        return (this._returnReasonOptions ?? []).map((returnReason) => ({
            id: returnReason.id,
            label: returnReason.name,
        }));
    }

    public get selectedReturnReasonLabel(): string | undefined {
        const selectedOption = this.returnReasonFilterOptions.find(
            (o: IRmaListFilterControlOption) => o.id === this.selectedReturnReasonId,
        );

        if (!selectedOption) {
            return undefined;
        }

        return selectedOption.label;
    }

    public get selectedReturnReasonValue() {
        if (!this.selectedReturnReasonId) {
            return this._allOption;
        }
        return this.returnReasonsOptions?.find(res => res.value === this.selectedReturnReasonId)
    }

    get restockValidatedOptions() {
        return [
            {
                value: RESTOCK_VALIDATED_TYPE.ALL,
                label: dashboardTranslationsStore.getTranslation('ALL', this._scope)
            },
            {
                value: RESTOCK_VALIDATED_TYPE.VALIDATED,
                label: dashboardTranslationsStore.getTranslation('VALIDATED', this._scope)
            },
            {
                value: RESTOCK_VALIDATED_TYPE.PARTIALLY_VALIDATED,
                label: dashboardTranslationsStore.getTranslation('PARTIALLY_VALIDATED', this._scope)
            },
            {
                value: RESTOCK_VALIDATED_TYPE.NOT_VALIDATED,
                label: dashboardTranslationsStore.getTranslation('NOT_VALIDATED', this._scope)
            }
        ];
    }

    /** Options for the redesigned sidebar validated filter (excludes the synthetic "All" option). */
    public get restockValidatedFilterOptions(): IRmaListFilterControlOption[] {
        this._ensureRestockValidatedFilterOptions();
        return this._restockValidatedFilterOptions;
    }

    public get selectedRestockValidatedLabel(): string | undefined {
        return this._selectedRestockValidatedOption?.label;
    }

    get restockValidatedValue() {
        return this.restockValidatedOptions?.find(res => res.value === this.restockValidatedType)
    }

    get reserverdItemExpirationOptions() {
        return [
            {
                value: RESERVED_ITEM_EXPIRATION_TYPE.ALL,
                label: dashboardTranslationsStore.getTranslation('ALL', this._scope)
            },
            {
                value: RESERVED_ITEM_EXPIRATION_TYPE.DATE_RANGE,
                label: 'By date range'
            },
            {
                value: RESERVED_ITEM_EXPIRATION_TYPE.DAYS_REMAINING,
                label: 'By days remaining'
            },
            {
                value: RESERVED_ITEM_EXPIRATION_TYPE.EXPIRED,
                label: 'Expired'
            },
        ];
    }

    get reserverdItemExpirationValue() {
        return this.reserverdItemExpirationOptions?.find(res => res.value === this.reserveItemExpiration)
    }

    /** Redesigned-page-only "Action required" buckets; see RmaListBucketFiltersStore. */
    public get bucketFilters(): RmaListBucketFiltersStore {
        return this._bucketFilters;
    }

    public get reservedItemDaysRemainingStore(): ReservedItemDaysRemainingStore {
        return this._reservedItemDaysRemainingStore;
    }

    get paymentStatusFilterOptions() {
        return [
            {
                value: PAYMENT_STATUS_TYPE.ALL,
                label: dashboardTranslationsStore.getTranslation('ALL', this._scope)
            },
            {
                value: PAYMENT_STATUS_TYPE.NOT_REFUNDED,
                label: dashboardTranslationsStore.getTranslation('PAYMENT_STATUS_NOT_REFUNDED', this._scope)
            },
            {
                value: PAYMENT_STATUS_TYPE.REFUNDED,
                label: dashboardTranslationsStore.getTranslation('PAYMENT_STATUS_REFUNDED', this._scope)
            },
            {
                value: PAYMENT_STATUS_TYPE.NOT_PAID,
                label: dashboardTranslationsStore.getTranslation('PAYMENT_STATUS_NOT_PAID', this._scope)
            },
            {
                value: PAYMENT_STATUS_TYPE.PAID,
                label: dashboardTranslationsStore.getTranslation('PAYMENT_STATUS_PAID', this._scope)
            },
            {
                value: PAYMENT_STATUS_TYPE.EXPIRING,
                label: dashboardTranslationsStore.getTranslation('PAYMENT_STATUS_EXPIRING', this._scope)
            }
        ];
    }

    public setSearchCustomerId(value: string) {
        this._changes.search_input = value;
        this._clearUnneededChanges();
    }

    public setSelectedResolutionId(value: number | null) {
        this._changes.filter_resolution_id = value;

        if (value === null) {
            this._selectedResolutionOption = null;
        } else {
            this._selectedResolutionOption = this._findResolutionFilterOption(value);
        }
        this._clearUnneededChanges();
    }

    public setSelectedReturnReasonId(value: number | null) {
        this._changes.filter_return_reason_id = value;
        this._clearUnneededChanges();
    }

    public setRestockValidatedType(value: RESTOCK_VALIDATED_TYPE) {
        this._changes.filter_restock_validated_type = value;

        if (value === RESTOCK_VALIDATED_TYPE.ALL) {
            this._selectedRestockValidatedOption = null;
        } else {
            this._selectedRestockValidatedOption = this._findRestockValidatedFilterOption(value);
        }
        this._clearUnneededChanges();
    }

    public setPaymentStatusType(value: PAYMENT_STATUS_TYPE) {
        this._changes.filter_payment_status_type = value;
        this._clearUnneededChanges();
    }

    public setReserverdItemExpiration(value: RESERVED_ITEM_EXPIRATION_TYPE) {
        this._changes.filter_reserve_item_expiration = value;
        this._clearUnneededChanges();
    }

    public setReserverdItemExpirationInput(value: string | undefined) {
        this._changes.filter_reserve_item_expiration_input = value;
        this._clearUnneededChanges();
    }

    public setDateRange(value: string) {
        this._isDefaultDateRange = false;
        this._changes.filter_date_range = value;
        this._clearUnneededChanges();
    }

    public setSelectedQuickFilter(value: RMA_QUICK_FILTERS) {
        this._changes.filter_status = value;
        this._clearUnneededChanges();
    }

    /** Selects a status chip, or clears it back to ALL when the same chip is clicked again. */
    public toggleSelectedQuickFilter(status: RMA_QUICK_FILTERS) {
        const nextStatus = this.selectedQuickFilter === status ? RMA_QUICK_FILTERS.ALL : status;
        this.setSelectedQuickFilter(nextStatus);
    }

    /**
     * Resets wired sidebar filters (status, date, resolution, return reason, validated, reserved item)
     * to defaults, plus both Action-required buckets.
     *
     * The bucket clearing is explicit now that the setters no longer do it. It belongs here: the
     * caller reloads straight after, so a surviving bucket would keep the list narrowed invisibly.
     */
    public clearAll() {
        this.setSelectedQuickFilter(RMA_QUICK_FILTERS.ALL);
        this.setDateRange("");
        this.setSelectedResolutionId(null);
        this.setSelectedReturnReasonId(null);
        this.setRestockValidatedType(RESTOCK_VALIDATED_TYPE.ALL);
        this.setReserverdItemExpiration(RESERVED_ITEM_EXPIRATION_TYPE.ALL);
        this.setReserverdItemExpirationInput("");
        this._reservedItemDaysRemainingStore.reset();
        this._bucketFilters.clearSelections();
        this._clearUnneededChanges();
    }

    /**
     * Sidebar selections shown as chips, in sidebar order. A filter can contribute the committed
     * chip, the draft chip, or both when the draft has replaced the committed value.
     * Status follows the tab: a status the current tab does not offer is omitted, matching the
     * RMA Status section. Reserved item is omitted unless the feature is allowed. Buckets are
     * excluded — they apply on click and render their own chips.
     */
    public collectSelectedFilterChips(translate: (key: string) => string, timeZone: string): ISelectedFilterChip[] {
        const visibleStatuses = this.getVisibleQuickFilterStatuses(translate);
        return [
            this._selectedFilterChips(
                SELECTED_FILTER_CHIP.STATUS,
                this._changes.filter_status === undefined,
                visibleStatuses.find((status) => status.id === this.selectedQuickFilter)?.title,
                visibleStatuses.find((status) => status.id === this._model.filter_status)?.title,
            ),
            this._selectedFilterChips(
                SELECTED_FILTER_CHIP.DATE_RANGE,
                this._changes.filter_date_range === undefined,
                this._dateRangeChipLabel(this.dateRange, timeZone),
                this._dateRangeChipLabel(this._model.filter_date_range, timeZone),
            ),
            this._selectedFilterChips(
                SELECTED_FILTER_CHIP.RESOLUTION,
                this._changes.filter_resolution_id === undefined,
                this.selectedResolutionLabel,
                this._model.filter_resolution_id != null
                    ? this._findResolutionFilterOption(this._model.filter_resolution_id)?.label
                    : undefined,
            ),
            this._selectedFilterChips(
                SELECTED_FILTER_CHIP.RETURN_REASON,
                this._changes.filter_return_reason_id === undefined,
                this.selectedReturnReasonLabel,
                this.returnReasonFilterOptions.find(
                    (option) => option.id === this._model.filter_return_reason_id,
                )?.label,
            ),
            this._selectedFilterChips(
                SELECTED_FILTER_CHIP.VALIDATED,
                this._changes.filter_restock_validated_type === undefined,
                this.selectedRestockValidatedLabel,
                this._appliedRestockValidatedLabel(),
            ),
            currentPortalStore.isFeatureAllowed(FEATURES.RESERVE_ITEMS)
                ? this._selectedFilterChips(
                    SELECTED_FILTER_CHIP.RESERVED_ITEM,
                    this._isReservedItemDraftUnchanged(),
                    this._draftReservedItemChipLabel(timeZone),
                    this._appliedReservedItemChipLabel(timeZone),
                )
                : null,
        ].reduce<ISelectedFilterChip[]>(
            (chips, next) => (next === null ? chips : chips.concat(next)),
            [],
        );
    }

    /**
     * Chips for one sidebar filter. Null when it has nothing to show. One chip when draft and
     * committed match; otherwise the committed value (if any) and the draft value (if any).
     */
    private _selectedFilterChips(
        id: SELECTED_FILTER_CHIP,
        unchanged: boolean,
        draftLabel?: string,
        appliedLabel?: string,
    ): ISelectedFilterChip[] | null {
        if (unchanged) {
            return draftLabel
                ? [{ id, label: draftLabel, state: SELECTED_FILTER_CHIP_STATE.APPLIED }]
                : null;
        }
        const chips = [
            appliedLabel
                ? { id, label: appliedLabel, state: SELECTED_FILTER_CHIP_STATE.REMOVED }
                : null,
            draftLabel
                ? { id, label: draftLabel, state: SELECTED_FILTER_CHIP_STATE.DRAFT }
                : null,
        ].filter((chip): chip is ISelectedFilterChip => chip !== null);
        return chips.length > 0 ? chips : null;
    }

    private _appliedRestockValidatedLabel(): string | undefined {
        const appliedType = this._model.filter_restock_validated_type;
        if (!appliedType || appliedType === RESTOCK_VALIDATED_TYPE.ALL) {
            return undefined;
        }
        return this._findRestockValidatedFilterOption(appliedType)?.label;
    }

    private _isReservedItemDraftUnchanged(): boolean {
        return this._changes.filter_reserve_item_expiration === undefined
            && this._changes.filter_reserve_item_expiration_input === undefined
            && !this._reservedItemDaysRemainingStore.hasChanges();
    }

    private _draftReservedItemChipLabel(timeZone: string): string | undefined {
        return getReservedItemSelectedLabel({
            selectedId: this.reserveItemExpiration,
            expiredLabel: this.reserverdItemExpirationOptions.find(
                (option) => option.value === this.reserveItemExpiration,
            )?.label,
            daysFrom: this.reservedItemDaysRemainingStore.from,
            daysTo: this.reservedItemDaysRemainingStore.to,
            dateRangeIso: this.reserveItemExpirationInput || "",
            timeZone,
        });
    }

    private _appliedReservedItemChipLabel(timeZone: string): string | undefined {
        const selectedId = this._model.filter_reserve_item_expiration;
        if (!selectedId || selectedId === RESERVED_ITEM_EXPIRATION_TYPE.ALL) {
            return undefined;
        }
        const input = this._model.filter_reserve_item_expiration_input;
        const days = selectedId === RESERVED_ITEM_EXPIRATION_TYPE.DAYS_REMAINING
            ? this._parseDaysRemaining(input)
            : {};
        return getReservedItemSelectedLabel({
            selectedId,
            expiredLabel: this.reserverdItemExpirationOptions.find(
                (option) => option.value === selectedId,
            )?.label,
            daysFrom: days.from,
            daysTo: days.to,
            dateRangeIso: input || "",
            timeZone,
        });
    }

    private _parseDaysRemaining(input: string): { from?: number; to?: number } {
        const [from, to] = input.split("-");
        const fromNumber = Number(from);
        const toNumber = Number(to);
        if (Number.isNaN(fromNumber) || Number.isNaN(toNumber)) {
            return {};
        }
        return { from: fromNumber, to: toNumber };
    }

    /** Clears one draft sidebar filter. Does not apply; the list reloads only on Apply. */
    public clearSelectedFilterChip(chipId: SELECTED_FILTER_CHIP) {
        switch (chipId) {
            case SELECTED_FILTER_CHIP.STATUS:
                this.setSelectedQuickFilter(RMA_QUICK_FILTERS.ALL);
                return;
            case SELECTED_FILTER_CHIP.DATE_RANGE:
                this.setDateRange("");
                return;
            case SELECTED_FILTER_CHIP.RESOLUTION:
                this.setSelectedResolutionId(null);
                return;
            case SELECTED_FILTER_CHIP.RETURN_REASON:
                this.setSelectedReturnReasonId(null);
                return;
            case SELECTED_FILTER_CHIP.VALIDATED:
                this.setRestockValidatedType(RESTOCK_VALIDATED_TYPE.ALL);
                return;
            case SELECTED_FILTER_CHIP.RESERVED_ITEM:
                this.setReserverdItemExpiration(RESERVED_ITEM_EXPIRATION_TYPE.ALL);
                this.setReserverdItemExpirationInput("");
                // Drop the draft days only. A committed range must survive so the removed chip
                // can still show it until Apply.
                this.reservedItemDaysRemainingStore.discardChanges();
                return;
        }
    }

    public toggleSearchExactMatch() {
        this._changes.search_exact_match = !this.searchExactMatch;
        this._clearUnneededChanges();
    }

    /**
     * Switching the tab keeps the rest of the filters intact, but the currently selected status
     * chip may be incompatible with the new `status_group` (e.g. CLOSED tab + chip=open). The
     * server enforces this as a 400 cross-field check (rms-api: StatusGroupMatchesStatusConstraint),
     * so we proactively reset the chip to ALL when it would conflict.
     */
    public setCurrentTab(tab: IRmaListTab) {
        this._changes.status_group = tab;
        this._resetStatusFilterIfIncompatibleWithTab(tab);
        // The Actions-status buckets (Manual Actions and Errors) are only shown on the Open tab, so a
        // selection must not linger when the merchant moves to Closed/All.
        if (tab !== IRmaListTab.STATUS_GROUP_OPEN) {
            this._bucketFilters.clearSelections();
        }
        this._clearUnneededChanges();
    }

    /**
     * Resets `filter_status` to ALL when the currently pending/applied chip is not allowed on the
     * given tab. Prevents an rms-api 400 from the `status_group` + `status` cross-field validator.
     */
    private _resetStatusFilterIfIncompatibleWithTab(tab: IRmaListTab) {
        const currentStatus = (this._changes.filter_status ?? this._model.filter_status) as RMA_QUICK_FILTERS | undefined;
        if (currentStatus && !isQuickFilterAllowedOnTab(tab, currentStatus)) {
            this._changes.filter_status = RMA_QUICK_FILTERS.ALL;
        }
    }

    /**
     * Identity of the applied filters that scope the bucket counters.
     *
     * Reads `_model`, never `_changes`: nothing enters `_model` except through a commit, so a change
     * here can only mean a real Apply / search submit / Clear all. That is what keeps the counters from
     * refetching on every keystroke, page change or sort — no debounce needed.
     *
     * Excludes the tab and both bucket selections, which must not narrow a count that describes them.
     * Includes `filter_status`, which does narrow it.
     */
    public get appliedCountersScopeKey(): string {
        return JSON.stringify([
            this._model.search_input,
            this._model.search_exact_match,
            this._model.filter_resolution_id,
            this._model.filter_return_reason_id,
            this._model.filter_restock_validated_type,
            this._model.filter_payment_status_type,
            this._model.filter_date_range,
            this._model.filter_status,
            this._model.filter_reserve_item_expiration,
            this._model.filter_reserve_item_expiration_input,
        ]);
    }

    /** Explicit allow-list, never a spread: a spread would leak any filter key added later. */
    public buildCountersFilterArgs(): IFetchRmaFiltersArgs {
        return {
            search_input: this._model.search_input,
            search_exact_match: this._model.search_exact_match,
            filter_resolution_id: this._model.filter_resolution_id,
            filter_return_reason_id: this._model.filter_return_reason_id,
            filter_restock_validated_type: this._model.filter_restock_validated_type,
            filter_payment_status_type: this._model.filter_payment_status_type,
            filter_date_range: this._model.filter_date_range,
            filter_status: this._model.filter_status,
            filter_reserve_item_expiration: this._model.filter_reserve_item_expiration,
            filter_reserve_item_expiration_input: this._model.filter_reserve_item_expiration_input,
        };
    }

    /**
     * Filter-independent option lists, applied on the first load only: rebuilding them mid-session
     * re-runs `_syncSelectedResolutionOption()`, which can null an applied resolution's chip label.
     */
    private _applyStaticFilterOptions(filters: IRmaListFilterOptions): void {
        this._returnReasonOptions = filters.return_reasons;
        this._resolutionsPreset = new ResolutionsPreset(filters.resolutions);
        this._resolutionFilterGroups = this._buildResolutionFilterGroups();
        this._syncSelectedResolutionOption();
    }

    public async loadFilterOptions() {
        /** `_isLoaded` is only set once the response lands, so without `_isLoading` a double-mount double-fetches. */
        if (this._isLoaded || this._isLoading) {
            return;
        }

        const requestId = ++this._filterOptionsRequestId;
        const scopeKey = this.appliedCountersScopeKey;
        try {
            this._isLoading = true;
            const filters = await this._rmaListService.fetchFilters(this.backend, this.buildCountersFilterArgs());
            if (requestId !== this._filterOptionsRequestId) {
                return;
            }
            runInAction(() => {
                this._applyStaticFilterOptions(filters);
                this._bucketFilters.applyFirstLoad(filters, scopeKey);
                this._isLoading = false;
                this._isLoaded = true;
            });
            /**
             * Filters may have changed while this was in flight; self-guards on the scope key.
             *
             * Load-bearing on first load: the paged store's first fetch awaits
             * `ensureDefaultDateRangeTimeZone()`, which rewrites the seeded window into merchant TZ, so
             * without this the counters would describe the seeded one. Calling it from here instead
             * would not help — `currentPortalStore.load()` returns early while a call is in flight.
             */
            void this._bucketFilters.refreshCounters();
        } catch (error) {
            runInAction(() => {
                this._isLoading = false;
            });
            console.log(error);
        }
    }

    private _buildResolutionFilterGroups(): IRmaListFilterControlOptionGroup[] {
        return this._resolutionsPreset
            .getOptionsPreset()
            .map((group) => ({
                title: typeof group.category === "string" ? group.category : "",
                options: group.options
                    .filter((option) => option.value !== 0)
                    .map((option) => ({
                        id: Number(option.value),
                        label: String(option.label ?? ""),
                    })),
            }))
            .filter((group) => group.options.length > 0);
    }

    private _findResolutionFilterOption(id: number): IRmaListFilterControlOption | null {
        return this._resolutionFilterGroups
            .flatMap((group) => group.options)
            .find((option) => option.id === id) ?? null;
    }

    private _syncSelectedResolutionOption() {
        const id = this.selectedResolutionId;
        if (id === null) {
            this._selectedResolutionOption = null;
        } else {
            this._selectedResolutionOption = this._findResolutionFilterOption(id);
        }
    }

    private _buildRestockValidatedFilterOptions(): IRmaListFilterControlOption[] {
        return this.restockValidatedOptions
            .filter((option) => option.value !== RESTOCK_VALIDATED_TYPE.ALL)
            .map((option) => ({
                id: option.value,
                label: option.label,
            }));
    }

    private _ensureRestockValidatedFilterOptions() {
        if (this._restockValidatedFilterOptions.length === 0) {
            this._restockValidatedFilterOptions = this._buildRestockValidatedFilterOptions();
        }
    }

    private _findRestockValidatedFilterOption(
        id: RESTOCK_VALIDATED_TYPE,
    ): IRmaListFilterControlOption | null {
        this._ensureRestockValidatedFilterOptions();
        return this._restockValidatedFilterOptions.find((option) => option.id === id) ?? null;
    }

    private _syncSelectedRestockValidatedOption() {
        const id = this.selectedRestockValidatedId;
        if (id === null) {
            this._selectedRestockValidatedOption = null;
        } else {
            this._selectedRestockValidatedOption = this._findRestockValidatedFilterOption(id);
        }
    }

    private _merchantTimeZone(): string {
        return currentPortalStore.setting?.timezoneStore?.timezone ?? FALLBACK_FORMAT_PARAMS.timeZone;
    }

    private _defaultDateRange(): string {
        return previousMonthsIsoRangeString(this._defaultDateRangeMonths!, this._merchantTimeZone());
    }

    /**
     * After portal settings load, rewrite the seeded Date created window in merchant TZ
     * if the merchant has not changed it. Call before the first list fetch.
     */
    public async ensureDefaultDateRangeTimeZone(): Promise<void> {
        if (!this._isDefaultDateRange) {
            return;
        }

        await currentPortalStore.load();
        const next = this._defaultDateRange();
        if (next !== this._model.filter_date_range) {
            this._model.filter_date_range = next;
        }
    }

    private _parseStatusGroupFromQuery(raw: string | null): IRmaListTab {
        if (raw === IRmaListTab.STATUS_GROUP_OPEN || raw === IRmaListTab.STATUS_GROUP_CLOSED || raw === IRmaListTab.STATUS_GROUP_ALL) {
            return raw;
        }
        return IRmaListTab.STATUS_GROUP_OPEN;
    }

    /** Prefer `search_input` when non-empty (after trim); otherwise fall back to trimmed `orderId`. */
    private _parseSearchInputFromQuery(params: URLSearchParams): string {
        const searchInput = params.get('search_input') ?? '';
        const orderId = params.get('orderId')?.trim() ?? '';

        let result = '';
        if (searchInput.trim()) {
            result = searchInput;
        } else if (orderId) {
            result = orderId;
        }

        return result;
    }

    private _createModelFromQuery(): IRmaListFilters {
        // Replace + with %2B before parsing to preserve literal + characters in search input
        // URLSearchParams decodes + as space per application/x-www-form-urlencoded standard
        const rawSearch = document.location.search.replace(/\+/g, '%2B');
        const params = new URLSearchParams(rawSearch);

        const filterStatus = params.get('filter_status');
        const searchInput = this._parseSearchInputFromQuery(params);
        const statusGroup = this._parseStatusGroupFromQuery(params.get("status_group"));

        return {
            search_input: searchInput,
            search_exact_match: false,
            filter_resolution_id: null,
            filter_return_reason_id: null,
            filter_restock_validated_type: RESTOCK_VALIDATED_TYPE.ALL,
            filter_date_range: this._isDefaultDateRange ? this._defaultDateRange() : "",
            filter_status: filterStatus || RMA_QUICK_FILTERS.OPEN,
            filter_reserve_item_expiration: RESERVED_ITEM_EXPIRATION_TYPE.ALL,
            filter_reserve_item_expiration_input: '',
            filter_payment_status_type: PAYMENT_STATUS_TYPE.ALL,
            /** Required by the type but never read: the bucket store owns the selection and
             *  `getUpdatedModel()` always projects it over these. */
            filter_manual_actions: [],
            filter_error_labels: [],
            filter_on_track: [],
            status_group: statusGroup,
        }
    }

    public hasChanges(): boolean {
        const reservedItemDaysRemainingChanges = this._reservedItemDaysRemainingStore.hasChanges();
        return (
            reservedItemDaysRemainingChanges ||
            this._bucketFilters.hasChanges() ||
            !!Object.keys(this._changes).length
        );
    }

    public discardChanges(): void {
        this._changes = {};
        this._reservedItemDaysRemainingStore.discardChanges();
        this._bucketFilters.discardChanges();
        this._syncSelectedResolutionOption();
        this._syncSelectedRestockValidatedOption();
    }

    public commitChanges(): void {
        this._reservedItemDaysRemainingStore.commitChanges();
        this._bucketFilters.commitChanges();
        this._model = this.getUpdatedModel();
        this.discardChanges();
    }

    /**
     * Applied filters plus the current (draft) bucket selection.
     *
     * Buckets apply on click while the sidebar is draft-until-Apply, so a bucket click must not drag
     * pending drafts onto the wire — hence the applied `_model` here rather than `getUpdatedModel()`.
     */
    public getFiltersForBucketApply(): IRmaListFilters {
        return { ...this._model, ...this._bucketFilters.getUpdatedModel() };
    }

    /** Commits only the bucket keys, so pending drafts stay pending and Apply (N) stays honest. */
    public commitBucketSelection(): void {
        this._bucketFilters.commitChanges();
    }

    public getUpdatedModel(): IRmaListFilters {
        const updatedModel = { ...this._model, ...this._changes, ...this._bucketFilters.getUpdatedModel() };
        if (this.reserveItemExpiration === RESERVED_ITEM_EXPIRATION_TYPE.DAYS_REMAINING) {
            const daysRemainingModel = this._reservedItemDaysRemainingStore.getUpdatedModel();
            updatedModel.filter_reserve_item_expiration_input = `${daysRemainingModel.from}-${daysRemainingModel.to}`;
        }

        return updatedModel;
    }

    /**
     * Committed filters that currently define list membership (no drafts, no page/sort).
     * Bucket keys come from the bucket store's applied state — `commitBucketSelection` does not
     * rewrite `_model`, so reading buckets from `_model` alone would be stale.
     */
    public getAppliedFilters(): IRmaListFilters {
        return {
            search_input: this._model.search_input,
            search_exact_match: this._model.search_exact_match,
            filter_resolution_id: this._model.filter_resolution_id,
            filter_return_reason_id: this._model.filter_return_reason_id,
            filter_restock_validated_type: this._model.filter_restock_validated_type,
            filter_reserve_item_expiration: this._model.filter_reserve_item_expiration,
            filter_date_range: this._model.filter_date_range,
            filter_reserve_item_expiration_input: this._model.filter_reserve_item_expiration_input,
            filter_status: this._model.filter_status,
            filter_payment_status_type: this._model.filter_payment_status_type,
            filter_manual_actions: [...this._bucketFilters.appliedManualActions],
            filter_error_labels: [...this._bucketFilters.appliedErrorLabels],
            filter_on_track: [...this._bucketFilters.appliedOnTrack],
            status_group: this._model.status_group,
        };
    }

    public getReportFilters(): IReportFiltersType {
        const filters: IReportFiltersType = {
            filter_is_restock_validated: this.restockValidatedType as string,
            filter_status: this.selectedQuickFilter as string,
            search_query: this.searchInput,
            is_exact_match: this.searchExactMatch ? '1' : '0'
        }
        if (this.selectedResolutionId) {
            filters['filter_resolution'] = this.selectedResolutionId.toString();
        }
        if (this.selectedReturnReasonId) {
            filters['filter_return_reason'] = this.selectedReturnReasonId.toString();
        }
        if (this.dateRange) {
            filters['filter_date'] = this.dateRange;
        }
        if (this.reserveItemExpiration) {
            filters['filter_reserve_item_expiration'] = this.reserveItemExpiration;
        }
        if (this.reserveItemExpirationInput) {
            filters['filter_reserve_item_expiration_input'] = this.reserveItemExpirationInput;
        }
        if (this.paymentStatus) {
            filters['filter_payment_status_type'] = this.paymentStatus;
        }
        return filters;
    }

    public hasActiveDrawerFilters(): boolean {
        const filters = this.getUpdatedModel();
        const {
            search_exact_match,
            search_input,
            filter_status,
            // Because status group is only used in Redesigned Manage returns list page
            // and this method (hasActiveDrawerFilters) is used for legacy page - excluding status_group here to avoid
            // incorrect output
            status_group: _omitStatusGroup,
            // Manual actions are a redesigned-page-only filter (and an empty array is truthy), so they
            // must not count as an active drawer filter on the legacy page.
            filter_manual_actions: _omitManualActions,
            // Error-bucket labels are likewise a redesigned-page-only filter (empty array is truthy).
            filter_error_labels: _omitErrorLabels,
            // On track sections, same story.
            filter_on_track: _omitOnTrack,
            ...drawerFilters
        } = filters;
        return Object.values(drawerFilters).some(filter => (filter && filter !== RESTOCK_VALIDATED_TYPE.ALL));
    }

    private _clearUnneededChanges() {
        Object.keys(this._changes).forEach(key => {
            const theKey = key as keyof IRmaListFilters;
            const changed = this._changes[theKey];
            const applied = this._model?.[theKey];
            if (changed === applied) {
                delete this._changes[theKey];
            }
        });
    }

}

export default RmaListFiltersStore;
